<script lang="ts">
  import { t } from '$core/i18n';
  import {
    searchUsers,
    sendFriendRequest,
    type PublicUser
  } from '$features/social';
  import { Modal } from '$ui/primitives/Modal';
  import { Button } from '$ui/primitives/Button';
  import { Input } from '$ui/primitives/Input';
  import { Avatar } from '$ui/primitives/Avatar';
  import { EmptyState } from '$ui/primitives/EmptyState';

  let {
    onClose,
    onSent
  }: {
    onClose: () => void;
    onSent: () => void;
  } = $props();

  let query = $state('');
  let results = $state<PublicUser[]>([]);
  let searching = $state(false);
  let sending = $state<string | null>(null);
  let message = $state<string | null>(null);
  let searched = $state(false);
  let sentIds = $state<Set<string>>(new Set());

  async function doSearch(): Promise<void> {
    const q = query.trim();
    if (q.length < 1) return;
    searching = true;
    message = null;
    try {
      results = await searchUsers(q);
      searched = true;
    } finally {
      searching = false;
    }
  }

  async function send(userId: string): Promise<void> {
    if (sending) return;
    sending = userId;
    message = null;
    try {
      const res = await sendFriendRequest(userId);
      if (res.ok) {
        message = t('friends.sentOk');
        sentIds = new Set([...sentIds, userId]);
        onSent();
      } else if (res.error.includes('duplicate') || res.error.includes('unique')) {
        message = t('friends.sentAlready');
        sentIds = new Set([...sentIds, userId]);
      } else {
        message = t('friends.sentError');
      }
    } finally {
      sending = null;
    }
  }

  function shortCountry(c: string): string {
    return c === 'pirate' ? t('leaderboard.countryPirate') : c.toUpperCase();
  }

  let open = $state(true);
</script>

<Modal
  bind:open
  title={t('friends.addTitle')}
  size="md"
  onclose={onClose}
>
  <div class="body">
    <p class="hint">{t('friends.addHint')}</p>

    <div class="search-row">
      <Input
        type="text"
        placeholder={t('friends.searchPlaceholder')}
        bind:value={query}
        onkeydown={(e) => e.key === 'Enter' && void doSearch()}
      />
      <Button variant="filled" loading={searching} disabled={searching} onclick={() => void doSearch()}>
        {searching ? t('friends.searching') : t('friends.search')}
      </Button>
    </div>

    {#if message}
      <p class="info">{message}</p>
    {/if}

    {#if searched && results.length === 0}
      <EmptyState
        icon="search-x"
        title={t('friends.noResults')}
        variant="filter-empty"
      />
    {:else if results.length > 0}
      <ul class="results">
        {#each results as user (user.userId)}
          <li class="result">
            <div class="result-info">
              <Avatar src={user.avatarUrl} name={user.displayName} size="md" />
              <div class="result-texts">
                <span class="result-name">{user.displayName}</span>
                <span class="result-country">{shortCountry(user.country)}</span>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={sending === user.userId || sentIds.has(user.userId)}
              onclick={() => void send(user.userId)}
            >
              {sentIds.has(user.userId)
                ? t('friends.sentAlready')
                : sending === user.userId
                  ? t('friends.sending')
                  : t('friends.sendRequest')}
            </Button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .search-row {
    display: flex;
    gap: var(--space-2);
    align-items: flex-start;
  }

  .search-row :global(.field) {
    flex: 1;
  }

  .info {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-accent);
  }

  .results {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .result {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
    padding: var(--space-3);
    background: var(--color-surface-sunken);
    border-radius: var(--radius-md);
  }

  .result-info {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 0;
  }

  .result-texts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .result-name {
    font-size: var(--text-body);
    font-weight: var(--weight-medium);
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-country {
    font-size: var(--text-caption);
    color: var(--color-text-muted);
    font-family: var(--font-mono);
  }
</style>